/**
 * tokenService.js — client-side JWT inspection
 *
 * The backend signs JWTs; the client never verifies signatures (it can't,
 * and doesn't need to — the server rejects bad tokens). What the client
 * CAN do is read the expiry claim so it never *pretends* to be signed in
 * with a token the server is guaranteed to reject. That pretend-login was
 * the build-32 bug: Face ID replayed a long-expired token from the
 * Keychain, the home screen rendered, and the visit upload died with
 * "Invalid or expired token".
 */

/**
 * Decode a JWT payload without verifying the signature.
 * Returns the claims object, or null if the token is malformed.
 */
export function decodeJwtPayload(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    // base64url → base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/**
 * True if the token is expired (or close enough that an API call made now
 * would land after expiry), or unreadable. A malformed token is treated as
 * expired — the server would reject it anyway.
 *
 * @param {string} token
 * @param {number} [skewMs] - safety margin, default 60s
 */
export function isTokenExpired(token, skewMs = 60 * 1000) {
  if (!token) return true;
  const claims = decodeJwtPayload(token);
  if (!claims || !claims.exp) return true;
  return claims.exp * 1000 < Date.now() + skewMs;
}
